"use client";
/*
 * QueryPriorityBadge — Badge component for Query Priority
 * Handles priority variants: high (red), medium (yellow), low (green)
 */

import React from "react";
import * as SubframeCore from "@subframe/core";
import * as SubframeUtils from "../utils";

interface QueryPriorityBadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  priority: "low" | "medium" | "high";
  icon?: React.ReactNode;
  children?: React.ReactNode;
  iconRight?: React.ReactNode;
  className?: string;
}

const QueryPriorityBadge = React.forwardRef<HTMLDivElement, QueryPriorityBadgeProps>(
  function QueryPriorityBadge(
    {
      priority,
      icon = null,
      children,
      iconRight = null,
      className,
      ...otherProps
    }: QueryPriorityBadgeProps,
    ref
  ) {
    // Determine background and border colors based on priority
    const getBackgroundClasses = () => {
      switch (priority) {
        case "high":
          return "border-red-200 bg-red-100";
        case "medium":
          return "border-yellow-200 bg-yellow-100";
        case "low":
          return "border-green-200 bg-green-100";
        default:
          return "border-gray-200 bg-gray-100";
      }
    };

    // Determine text color based on priority
    const getTextColor = () => {
      switch (priority) {
        case "high":
          return "text-red-700";
        case "medium":
          return "text-yellow-700";
        case "low":
          return "text-green-700";
        default:
          return "text-gray-700";
      }
    };

    // Determine icon color based on priority
    const getIconColor = () => {
      switch (priority) {
        case "high":
          return "text-red-700";
        case "medium":
          return "text-yellow-700";
        case "low":
          return "text-green-700";
        default:
          return "text-gray-700";
      }
    };

    return (
      <div
        className={SubframeUtils.twClassNames(
          "group/query-priority-badge flex h-6 items-center gap-1 rounded-md border border-solid px-2",
          getBackgroundClasses(),
          className
        )}
        ref={ref}
        {...otherProps}
      >
        {icon ? (
          <SubframeCore.IconWrapper
            className={SubframeUtils.twClassNames(
              "text-caption font-caption",
              getIconColor()
            )}
          >
            {icon}
          </SubframeCore.IconWrapper>
        ) : null}
        {children ? (
          <span
            className={SubframeUtils.twClassNames(
              "whitespace-nowrap text-caption font-caption",
              getTextColor()
            )}
          >
            {children}
          </span>
        ) : null}
        {iconRight ? (
          <SubframeCore.IconWrapper
            className={SubframeUtils.twClassNames(
              "text-caption font-caption",
              getIconColor()
            )}
          >
            {iconRight}
          </SubframeCore.IconWrapper>
        ) : null}
      </div>
    );
  }
);

export { QueryPriorityBadge };

